// frontend/src/pages/checkout/i18n.ts
//
// 托管收银台（cashier）多语言字典 + 翻译函数 + 语言检测。
//
// 设计目标：
//   1. 白标面向国际：默认英文兜底（en），支持中文（zh）。
//   2. 结构可扩展：以后加语言（ja/ko/es 等）只需在 DICT 里补一份同 key 的字典，
//      并按需在 detectLang / LANG_OPTIONS 里登记即可，调用处无需改动。
//   3. 带变量的文案用占位符 {name}，运行时由 t(key, lang, vars) 替换。
//
// 仅服务于 token 收银会话流（CheckoutSessionFlow）与外壳/信任区的买家可见文案；
// 旧 payment-intent 流不接入（保持原中文）。

// 支持的语言代码。新增语言时在此追加，并补 DICT[新语言]。
export type CheckoutLang = 'en' | 'zh';

// 翻译字典的 key 集合（用 zh 字典推导类型，保证各语言 key 对齐时有提示）。
export type TransKey = keyof typeof DICT_EN;

// localStorage 持久化 key（与主题区分开）。
export const LANG_STORAGE_KEY = 'fs_checkout_lang';

// —— 主题（明暗）—— 与语言一起由上层统一管理，便于触发卡字段重挂。
export type CheckoutTheme = 'light' | 'dark';
export const THEME_STORAGE_KEY = 'fs_checkout_theme';

// 主题检测：手动选择（localStorage）优先于系统 prefers-color-scheme，最后兜底亮色。
export const detectTheme = (): CheckoutTheme => {
  if (typeof window !== 'undefined') {
    try {
      const saved = window.localStorage.getItem(THEME_STORAGE_KEY);
      if (saved === 'dark' || saved === 'light') return saved;
    } catch {
      /* ignore */
    }
    try {
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
      }
    } catch {
      /* ignore */
    }
  }
  return 'light';
};

// 语言切换器选项（顺序即展示顺序）。
export const LANG_OPTIONS: { value: CheckoutLang; label: string }[] = [
  { value: 'zh', label: '中文' },
  { value: 'en', label: 'English' },
];

// —— 英文字典（默认兜底）——
const DICT_EN = {
  // 外壳 / 品牌区
  shell_secure: 'Secure encrypted checkout',
  shell_provided_by: 'Provided by {name}',
  shell_footer: 'Secure payment · Your payment details are encrypted end-to-end',
  // 语言 / 主题切换器无障碍标签
  lang_switch_aria: 'Select language',
  theme_toggle_to_dark: 'Switch to dark mode',
  theme_toggle_to_light: 'Switch to light mode',

  // 信任区（左栏底部）
  trust_encryption: 'Payment data encrypted in transit · PCI-DSS compliant',
  trust_brands_aria: 'Accepted payment methods',
  trust_help: 'Contact the merchant if you have any questions.',

  // 加载 / 结果态
  result_pay_success_title: 'Payment successful',
  result_pay_success_subtitle:
    'Your payment is complete. No need to pay again. Contact the merchant if you have questions.',
  result_view_detail: 'View details',
  result_processing_title: 'Payment processing',
  result_processing_subtitle:
    'We are confirming your payment. The result will be sent via email / merchant notification. Please do not pay again.',
  result_failed_title: 'Payment not completed',
  result_failed_subtitle:
    'This payment did not succeed or was canceled. You can return to checkout and try again.',
  result_retry_pay: 'Pay again',
  load_error_title: 'Unable to open checkout',
  load_error_subtitle:
    'This checkout session does not exist or has expired. Please return to the merchant to start a new payment.',
  retry: 'Retry',
  session_complete_title: 'This order has already been paid',
  session_complete_subtitle: 'No need to pay again. Contact the merchant if you have questions.',
  session_processing_title: 'Payment processing',
  session_processing_subtitle:
    'We are confirming your payment. The result will be sent via email / merchant notification. Please do not pay again.',
  session_expired_title: 'This checkout session has expired',
  session_expired_subtitle: 'Please return to the merchant to start a new payment.',
  session_canceled_title: 'This checkout session was canceled',
  session_canceled_subtitle: 'Please return to the merchant to start a new payment.',
  session_unavailable_title: 'This checkout session is unavailable',
  session_unavailable_subtitle: 'Please return to the merchant to start a new payment.',
  requires_action_title: 'Further verification required',
  requires_action_subtitle:
    'This payment requires additional verification (such as 3D Secure). Please follow your bank’s instructions. The result is confirmed by the merchant notification.',

  // 订单摘要
  summary_setup_label: 'Save a payment method',
  summary_amount_due: 'Amount due',
  summary_setup_free: 'Free',
  summary_setup_no_charge: 'You will not be charged now',
  item_default_name: 'Item',
  breakdown_subtotal: 'Subtotal',
  breakdown_tax: 'Tax',
  breakdown_total: 'Total',

  // 支付区
  pay_title_setup: 'Save a payment method',
  pay_title_payment: 'Choose a payment method',
  pay_title_confirm: 'Confirm order',
  pay_subtitle_setup:
    'Once saved, the merchant can charge you as agreed. You will not be charged now',
  pay_subtitle_subscription: 'After confirmation, the subscription will start and renew each period',
  pay_subtitle_payment: 'Your payment details are encrypted end-to-end and secure',
  customer_email: 'Customer email',

  // 方式列表 / 卡字段
  loading_component: 'Loading checkout component...',
  pm_list_aria: 'Choose a payment method',
  pm_card_label: 'Credit / debit card',
  // 「更多支付方式」入口（Payment Element：Klarna/Affirm/ACH/SEPA/iDEAL 等）
  more_methods: 'More payment methods',
  more_methods_subtitle: 'Pay with bank transfer, buy now pay later, and more',
  // 异步支付（扫码 / 加密 / 跳转）轮询等待文案
  poll_waiting: 'Waiting for your payment to complete...',
  wallet_divider: 'Or pay with',
  field_card_number: 'Card number',
  field_expiry: 'Expiry',
  field_cvc: 'Security code',
  component_load_failed: 'Failed to load payment component. Please refresh and try again.',
  init_pay_failed: 'Failed to initialize payment. Please refresh and try again.',

  // 确认按钮
  confirm_setup: 'Save payment method',
  confirm_subscription: 'Confirm subscription {amount}',
  confirm_payment: 'Confirm payment {amount}',
  btn_processing: 'Processing...',
  btn_loading_component: 'Loading checkout component...',
  cancel: 'Cancel',
  footer_secure: 'Secure encrypted payment',

  // 自有渠道
  own_initiating: 'Initiating payment...',
  own_method_unavailable: 'This payment method is not enabled. Please choose another.',
  own_initiate_failed: 'Failed to initiate payment. Please try again later or choose another method.',
  qr_scan_tip: 'Scan the code with your app to complete the payment',
  qr_generating: 'Generating QR code, please retry shortly',
  redirect_going: 'Click the button below to go to the payment page and complete your payment.',
  redirect_manual: 'Go to payment',
  crypto_network: 'Network',
  crypto_amount: 'Amount',
  crypto_address_label: 'Receiving address',
  copy_address: 'Copy address',
  copied: 'Copied',
  crypto_transfer_tip:
    'Please transfer using the network and amount above. The order updates automatically once received',
  crypto_preparing: 'Crypto channel is preparing',

  // 错误 / 提示（message.*）
  msg_pay_success: 'Payment successful!',
  msg_setup_success: 'Payment method saved!',
  msg_component_not_ready: 'The payment component is not ready yet, please wait',
  msg_not_initialized: 'Payment not initialized, please refresh and try again',
  msg_pay_failed: 'Payment failed',
  msg_card_failed: 'Payment failed, please check your card details and try again',
  msg_copy_failed: 'Copy failed, please select and copy the address manually',
  load_session_failed: 'Failed to load. This checkout session may not exist or has expired',

  // mode 摘要文案
  mode_payment: 'One-time payment',
  mode_subscription: 'Subscription · auto-renews each period',
  mode_setup: 'Save a payment method',
} as const;

// —— 中文字典（与 DICT_EN key 一一对应）——
const DICT_ZH: Record<TransKey, string> = {
  shell_secure: '安全加密结算',
  shell_provided_by: '由 {name} 提供',
  shell_footer: '提供安全支付 · 您的支付信息全程加密',
  lang_switch_aria: '选择语言',
  theme_toggle_to_dark: '切换到暗色模式',
  theme_toggle_to_light: '切换到亮色模式',

  trust_encryption: '支付信息全程加密传输 · PCI-DSS 合规处理',
  trust_brands_aria: '受理的支付方式',
  trust_help: '如有疑问请联系商家。',

  result_pay_success_title: '支付成功',
  result_pay_success_subtitle: '您的付款已完成，无需重复支付。如有疑问请联系商家。',
  result_view_detail: '查看详情',
  result_processing_title: '支付处理中',
  result_processing_subtitle: '我们正在确认你的付款，结果会通过邮件 / 商家通知，请勿重复支付。',
  result_failed_title: '支付未完成',
  result_failed_subtitle: '本次支付未成功或被取消，你可以返回收银台重新发起支付。',
  result_retry_pay: '重新支付',
  load_error_title: '无法打开收银台',
  load_error_subtitle: '该收银会话不存在或已过期，请返回商家重新发起支付。',
  retry: '重试',
  session_complete_title: '该订单已完成支付',
  session_complete_subtitle: '无需重复支付，如有疑问请联系商家。',
  session_processing_title: '支付处理中',
  session_processing_subtitle: '我们正在确认你的付款，结果会通过邮件 / 商家通知，请勿重复支付。',
  session_expired_title: '该收银会话已过期',
  session_expired_subtitle: '请返回商家重新发起支付。',
  session_canceled_title: '该收银会话已取消',
  session_canceled_subtitle: '请返回商家重新发起支付。',
  session_unavailable_title: '该收银会话不可用',
  session_unavailable_subtitle: '请返回商家重新发起支付。',
  requires_action_title: '需要进一步验证',
  requires_action_subtitle:
    '本次支付需要额外的身份验证（如 3D Secure）。请按银行指引完成验证，支付结果以商家通知为准。',

  summary_setup_label: '绑定支付方式',
  summary_amount_due: '应付金额',
  summary_setup_free: '免费',
  summary_setup_no_charge: '本次不会扣款',
  item_default_name: '商品',
  breakdown_subtotal: '小计',
  breakdown_tax: '税费',
  breakdown_total: '合计',

  pay_title_setup: '绑定支付方式',
  pay_title_payment: '选择支付方式',
  pay_title_confirm: '确认订单',
  pay_subtitle_setup: '绑定后商家可按约定为您扣款，本次不会产生费用',
  pay_subtitle_subscription: '确认后将开通订阅，并按周期自动续费',
  pay_subtitle_payment: '您的支付信息全程加密，安全可靠',
  customer_email: '客户邮箱',

  loading_component: '正在加载收银组件...',
  pm_list_aria: '选择支付方式',
  pm_card_label: '信用卡 / 借记卡',
  more_methods: '更多支付方式',
  more_methods_subtitle: '银行转账、先买后付等更多方式',
  poll_waiting: '正在等待你的付款完成...',
  wallet_divider: '或使用以下方式支付',
  field_card_number: '卡号',
  field_expiry: '有效期',
  field_cvc: '安全码',
  component_load_failed: '支付组件加载失败，请刷新重试',
  init_pay_failed: '初始化支付失败，请刷新重试',

  confirm_setup: '绑定支付方式',
  confirm_subscription: '确认订阅 {amount}',
  confirm_payment: '确认支付 {amount}',
  btn_processing: '处理中...',
  btn_loading_component: '正在加载收银组件...',
  cancel: '取消',
  footer_secure: '安全加密支付',

  own_initiating: '正在发起支付...',
  own_method_unavailable: '此支付方式暂未开通，请选择其他方式',
  own_initiate_failed: '发起支付失败，请稍后重试或选择其他方式',
  qr_scan_tip: '请使用 App 扫码完成支付',
  qr_generating: '二维码生成中，请稍后重试',
  redirect_going: '请点击下方按钮前往支付页面完成支付。',
  redirect_manual: '前往支付',
  crypto_network: '转账网络',
  crypto_amount: '转账金额',
  crypto_address_label: '收款地址',
  copy_address: '复制地址',
  copied: '已复制',
  crypto_transfer_tip: '请按上述网络与金额完成转账，到账后订单将自动更新',
  crypto_preparing: '加密货币通道准备中',

  msg_pay_success: '支付成功!',
  msg_setup_success: '支付方式绑定成功!',
  msg_component_not_ready: '支付组件尚未就绪，请稍候',
  msg_not_initialized: '支付未初始化，请刷新重试',
  msg_pay_failed: '支付失败',
  msg_card_failed: '支付失败，请检查卡信息后重试',
  msg_copy_failed: '复制失败，请手动选择地址复制',
  load_session_failed: '加载失败，该收银会话可能不存在或已过期',

  mode_payment: '一次性付款',
  mode_subscription: '订阅 · 按周期自动续费',
  mode_setup: '绑定支付方式',
};

// 字典表：新增语言时在此登记。
const DICT: Record<CheckoutLang, Record<TransKey, string>> = {
  en: DICT_EN,
  zh: DICT_ZH,
};

// 默认语言（白标面向国际，兜底英文）。
export const DEFAULT_LANG: CheckoutLang = 'en';

// 翻译函数：取 lang 字典对应 key，缺失时回退英文，再回退 key 本身；
// vars 用于替换占位符 {x}。
export const t = (
  key: TransKey,
  lang: CheckoutLang,
  vars?: Record<string, string | number>
): string => {
  const dict = DICT[lang] || DICT[DEFAULT_LANG];
  let text = dict[key] ?? DICT[DEFAULT_LANG][key] ?? String(key);
  if (vars) {
    for (const k of Object.keys(vars)) {
      text = text.replace(new RegExp(`\\{${k}\\}`, 'g'), String(vars[k]));
    }
  }
  return text;
};

// 把任意输入归一化成受支持的语言（zh* → zh，其余 → en）。
const normalizeLang = (raw?: string | null): CheckoutLang | null => {
  if (!raw) return null;
  const v = raw.toLowerCase();
  if (v.startsWith('zh')) return 'zh';
  if (v.startsWith('en')) return 'en';
  return null;
};

// 语言检测优先级：URL ?lang= > localStorage > navigator.language > 默认 en。
// （localStorage 放在 navigator 之前，让用户的手动选择跨刷新生效。）
export const detectLang = (search?: string): CheckoutLang => {
  try {
    const params = new URLSearchParams(
      search ?? (typeof window !== 'undefined' ? window.location.search : '')
    );
    const fromUrl = normalizeLang(params.get('lang'));
    if (fromUrl) return fromUrl;
  } catch {
    /* ignore */
  }
  if (typeof window !== 'undefined') {
    try {
      const saved = normalizeLang(window.localStorage.getItem(LANG_STORAGE_KEY));
      if (saved) return saved;
    } catch {
      /* ignore */
    }
    const nav = normalizeLang(
      typeof navigator !== 'undefined' ? navigator.language : null
    );
    if (nav) return nav;
  }
  return DEFAULT_LANG;
};

// Stripe Card Elements 的 locale：与当前语言一致（zh→'zh'，en→'en'）。
export const stripeLocale = (lang: CheckoutLang): string => lang;
